import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { reviewPlatformKeys } from '@/lib/queryKeys';
import type { DisconnectOAuthDto, DisconnectPlatformDto } from '@/types/review-platform';

/**
 * Disconnect a connected platform (works for both OAuth and URL-paste rows).
 * POST /v2/review-platforms/settings/disconnect
 */
export function useDisconnectPlatform(locationId: string | null = null) {
  const queryClient = useQueryClient();
  return useApiMutation<{ ok: true }, DisconnectPlatformDto>({
    mutationFn: (dto) =>
      apiPost<{ ok: true }, DisconnectPlatformDto>(
        operationsClient,
        reviewPlatformsV2Endpoints.disconnect(),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: reviewPlatformKeys.settings(locationId),
      });
      toast.success('Platform disconnected');
    },
  });
}

/**
 * Revoke an OAuth token directly. Useful when the contractor wants to
 * disconnect even though the row currently has status NEEDS_REAUTH.
 * POST /v2/review-platforms/oauth/:platform/disconnect
 */
export function useDisconnectOAuth(
  platformSlug: 'google' | 'facebook',
  locationId: string | null = null,
) {
  const queryClient = useQueryClient();
  return useApiMutation<{ ok: true }, DisconnectOAuthDto>({
    mutationFn: (dto) =>
      apiPost<{ ok: true }, DisconnectOAuthDto>(
        operationsClient,
        reviewPlatformsV2Endpoints.oauthDisconnect(platformSlug),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: reviewPlatformKeys.settings(locationId),
      });
      toast.success('OAuth connection revoked');
    },
  });
}
